import { ref, readonly, computed } from '@vue/composition-api';
import { defineStore } from 'pinia';
import type { EditorBlock, Selection, TextMenuState } from '@getaccept/editor-lib';
import { Context, FontUpdateProgress } from '@getaccept/editor-lib';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  MenuCommand,
  ImageMenuState,
  Hover,
  ImageSearchState,
  NodeSettingsState,
  ImageSearchPayload,
} from '../types';
import { EditorBlockService } from '../api/services/editor-block.service';
import { EditorCommentsService } from '../api/services/editor-comments.service';
import type { UpdateRequest } from './editor-ui.types';
import { ContentRequestType } from './editor-ui.types';
import { getEditorBlockIdVersionMap } from './helpers/get-editor-block-id-version-map';
import { getCommentsWithNewEditorBlockVersion } from './helpers/get-comments-with-new-editor-block-version';
import { useEditorContentStore } from './editor-content.store';

export const useEditorUIStore = defineStore('editor-ui', () => {
  const context = ref(Context.Document);
  const fontUpdateProgress = ref<FontUpdateProgress>(undefined);
  const selected = ref<Selection>({
    nodeId: '',
    cellId: '',
    rowId: '',
    sectionId: '',
    contentId: '',
    subElementType: undefined,
    subElementRowId: undefined,
    subElementColumnId: undefined,
  });
  const hovered = ref<Hover>({
    nodeId: '',
    rowId: '',
    subElementType: undefined,
    subElementRowId: undefined,
  });
  const textMenuState = ref<TextMenuState>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    link: false,
    paragraph: false,
    heading1: false,
    heading2: false,
    subHeading: false,
    label: false,
    bulletList: false,
    orderedList: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false,
    alignJustify: false,
    listToggle: false,
    undo: false,
    redo: false,
    mergeTagId: '',
    linkUrl: '',
  });
  const imageMenuState = ref<ImageMenuState>({
    heading: false,
    caption: false,
  });
  const menuCommand = ref<MenuCommand>(undefined);
  const updateQueue = ref<UpdateRequest[]>([]);
  const isUpdating = ref<boolean>(false);
  const blocksToUpdate = ref<{ block: EditorBlock; noUpdate: boolean }[]>([]);
  const imageSearchState = ref<ImageSearchState>({
    images: [],
    total: undefined,
    totalPages: undefined,
    isLoading: false,
  });
  const nodeSettingsState = ref<NodeSettingsState>({
    nodeId: '',
    visible: false,
  });
  const alert = ref<{ message: string; contentUpdateFailed?: boolean }>(null);
  const isSigning = ref<boolean>(false);
  const viewUpdateFailed = ref<boolean>(null);
  const viewUpdateSuccess = ref<boolean>(null);
  const formDragAndDropActive = ref<boolean>(false);
  const formInputFieldsWithErrors = ref<string[]>([]);

  const setContext = (contextValue: Context) => {
    context.value = contextValue;
  };

  const setFontUpdateProgress = (progress: FontUpdateProgress) => {
    fontUpdateProgress.value = progress;
  };

  const setIsSigning = (isSigningValue: boolean) => {
    isSigning.value = isSigningValue;
  };

  const setSelection = (selection: Selection) => {
    selected.value = { ...selection };
  };

  const resetSelection = () => {
    selected.value = undefined;
  };

  const setHover = (hover: Hover) => {
    hovered.value = { ...hover };
  };

  const resetHover = () => {
    hovered.value = undefined;
  };

  const setFormDragAndDropActive = (value: boolean) => {
    formDragAndDropActive.value = value;
  };

  const updateTextMenuState = (state: TextMenuState) => {
    textMenuState.value = { ...state };
  };

  const updateImageMenuState = (state: Partial<ImageMenuState>) => {
    const initialState = {
      heading: false,
      caption: false,
    };
    imageMenuState.value = { ...initialState, ...state };
  };

  const triggerMenuCommand = (command: MenuCommand) => {
    menuCommand.value = command;
  };

  const setBlockToUpdate = (block: EditorBlock, noUpdate?: boolean) => {
    blocksToUpdate.value = [...blocksToUpdate.value, { block, noUpdate }];
  };

  const removeBlockToUpdate = (id: string) => {
    const indexToDelete = blocksToUpdate.value.findIndex(({ block }) => block?.id === id);
    if (indexToDelete === -1) {
      return;
    }
    blocksToUpdate.value = [
      ...blocksToUpdate.value.slice(0, indexToDelete),
      ...blocksToUpdate.value.slice(indexToDelete + 1),
    ];
  };

  const setViewUpdateFailed = (value: boolean) => {
    viewUpdateFailed.value = value;
  };

  const setViewUpdateSuccess = (value: boolean) => {
    viewUpdateSuccess.value = value;
  };

  const processQueue = async () => {
    isUpdating.value = true;
    while (updateQueue.value.length > 0) {
      const { type, payload, blocksBeforeUpdate } = updateQueue.value[0];

      try {
        await EditorBlockService.dispatchRequest(payload, type);

        if (payload.updateCommentsDto?.comments.length) {
          const id = context.value !== Context.DsrEdit ? payload.documentId : payload.dealroomId;
          const versions = await EditorBlockService.loadDocumentEditorContentVersions(id);

          const idVersionMap = getEditorBlockIdVersionMap(
            payload.updateCommentsDto.comments,
            versions
          );

          useEditorContentStore().setEditorBlockVersions(idVersionMap);

          const commentsWithNewVersions = getCommentsWithNewEditorBlockVersion(
            payload.updateCommentsDto.comments,
            idVersionMap
          );

          if (context.value !== Context.DsrEdit) {
            await EditorCommentsService.updateMany(payload.documentId, commentsWithNewVersions);
          } else {
            await EditorCommentsService.updateManyDealroomSelections({
              editorSelections: commentsWithNewVersions.comments.flatMap(
                comment => comment.editorSelection
              ),
            });
          }
        }

        if (type === ContentRequestType.UpdateContentFontSettings) {
          setFontUpdateProgress(FontUpdateProgress.Succeeded);
        }

        updateQueue.value.shift();

        if (context.value === Context.DocumentView) {
          setViewUpdateSuccess(true);
        }
      } catch (error) {
        if (type === ContentRequestType.UpdateContentFontSettings) {
          setFontUpdateProgress(FontUpdateProgress.Failed);
        }

        updateQueue.value = [];

        if (context.value === Context.DocumentView && isSigning.value) {
          setViewUpdateFailed(true);
        } else {
          useEditorContentStore().contentUpdateFailed(blocksBeforeUpdate, error?.message);
        }
      }
    }
    isUpdating.value = false;
  };

  const enqueueRequest = (request: UpdateRequest) => {
    updateQueue.value = [...updateQueue.value, request];
    if (!isUpdating.value) {
      processQueue();
    }
  };

  const hasFormInputFieldsWithErrors = computed(() => formInputFieldsWithErrors.value.length > 0);

  const resetFormInputFieldsValidation = () => {
    formInputFieldsWithErrors.value = [];
  };

  const updateFormInputFieldsValidationStatus = ({
    fieldId,
    isValid,
  }: {
    fieldId: string;
    isValid: boolean;
  }) => {
    if (isValid) {
      formInputFieldsWithErrors.value = formInputFieldsWithErrors.value.filter(
        id => id !== fieldId
      );

      return;
    }

    if (formInputFieldsWithErrors.value.includes(fieldId)) {
      return;
    }

    formInputFieldsWithErrors.value = [...formInputFieldsWithErrors.value, fieldId];
  };

  const setAlert = (payload: { message: string; contentUpdateFailed?: boolean }) => {
    alert.value = payload;
  };

  const imageSearch = async (payload: ImageSearchPayload) => {
    imageSearchState.value.isLoading = true;

    try {
      const imageLibrarySearch = await EditorBlockService.imageLibrarySearch(payload);
      imageSearchState.value = {
        ...imageSearchState.value,
        images: [...imageSearchState.value.images, ...imageLibrarySearch.images],
        totalPages: imageLibrarySearch.totalPages,
        total: imageLibrarySearch.total,
      };
    } catch (error) {
      setAlert({
        message: t('Failed-to-search-images'),
      });
    } finally {
      imageSearchState.value.isLoading = false;
    }
  };

  const imageSearchReset = () => {
    imageSearchState.value = {
      ...imageSearchState.value,
      images: [],
      total: undefined,
      totalPages: undefined,
    };
  };

  const trackDownload = async (id: string) => {
    try {
      await EditorBlockService.trackImageLibraryDownload(id);
    } catch (error) {
      console.error(error);
    }
  };

  const updateNodeSettingsState = (state: NodeSettingsState) => {
    nodeSettingsState.value = { ...state };
  };

  return {
    context: readonly(context),
    selected: readonly(selected),
    hovered: readonly(hovered),
    textMenuState: readonly(textMenuState),
    imageMenuState: readonly(imageMenuState),
    menuCommand: readonly(menuCommand),
    blocksToUpdate: readonly(blocksToUpdate),
    isUpdating: readonly(isUpdating),
    viewUpdateFailed: readonly(viewUpdateFailed),
    viewUpdateSuccess: readonly(viewUpdateSuccess),
    alert: readonly(alert),
    imageSearchState: readonly(imageSearchState),
    nodeSettingsState: readonly(nodeSettingsState),
    fontUpdateProgress: readonly(fontUpdateProgress),
    isSigning: readonly(isSigning),
    formDragAndDropActive: readonly(formDragAndDropActive),
    updateQueue: readonly(updateQueue),
    hasFormInputFieldsWithErrors: readonly(hasFormInputFieldsWithErrors),

    setContext,
    setFontUpdateProgress,
    setIsSigning,
    setSelection,
    resetSelection,
    setHover,
    resetHover,
    setFormDragAndDropActive,
    updateTextMenuState,
    updateImageMenuState,
    triggerMenuCommand,
    setBlockToUpdate,
    removeBlockToUpdate,
    setViewUpdateFailed,
    setViewUpdateSuccess,
    enqueueRequest,
    setAlert,
    imageSearch,
    imageSearchReset,
    trackDownload,
    updateNodeSettingsState,

    processQueue,
    updateFormInputFieldsValidationStatus,
    resetFormInputFieldsValidation,
  };
});
